import { useState, useCallback, useMemo } from 'react';
import useAppSnackbar from '../useAppSnackbar';
import { getEmployeesSummary } from '../../services/api/reportsServices';
import { EmployeesSummary, ReportFilters } from '../../models/reports';
import { DEFAULT_PAGE_SIZE } from '../../constants';

interface GetEmployeesSummaryReturn {
  items: EmployeesSummary[];
  isLoading: boolean;
  isFetching: boolean;
  fetchData: (
    payload: ReportFilters,
    loggedHoursMismatch: boolean,
    viewShowInactiveEmployees: boolean
  ) => void;
  fetchEmployeeData: (
    payload: ReportFilters,
    loggedHoursMismatch: boolean,
    viewShowInactiveEmployees: boolean
  ) => void;
  isEmpty: boolean;
  hasMore: boolean;
}

const defaultPayloadData = {
  skip: null,
  take: DEFAULT_PAGE_SIZE,
  filter: null,
  includeInactive: false,
  includeDeleted: false,
};

export function useEmployeesSummary(): GetEmployeesSummaryReturn {
  const [items, setItems] = useState<EmployeesSummary[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const { onErrorSnackbar } = useAppSnackbar();

  const fetchData = useCallback(
    (
      payloadParam: ReportFilters,
      loggedHoursMismatch,
      viewShowInactiveEmployees
    ) => {
      if (!payloadParam.skip) {
        setIsLoading(true);
      } else {
        setIsFetching(true);
      }
      getEmployeesSummary(
        {
          ...defaultPayloadData,
          includeDeleted: viewShowInactiveEmployees,
          includeInactive: viewShowInactiveEmployees,
          ...payloadParam,
        },
        loggedHoursMismatch
      )
        .then((resp) => {
          if (payloadParam.skip) {
            setItems((prevState) => [...prevState, ...resp.items]);
          } else {
            setItems([...resp.items]);
          }

          const doesHaveMore =
            resp.totalCount - resp.items.length - (payloadParam.skip || 0) > 0;

          setHasMore(doesHaveMore);
        })
        .catch(onErrorSnackbar)
        .finally(() => {
          setIsLoading(false);
          setIsFetching(false);
        });
    },
    [onErrorSnackbar]
  );

  const fetchEmployeeData = useCallback(
    (
      payloadParam: ReportFilters,
      loggedHoursMismatch,
      viewShowInactiveEmployees
    ) => {
      setIsLoading(true);
      getEmployeesSummary(
        {
          ...defaultPayloadData,
          includeDeleted: viewShowInactiveEmployees,
          includeInactive: viewShowInactiveEmployees,
          ...payloadParam,
        },
        loggedHoursMismatch
      )
        .then((resp) => {
          setItems((prevState) => {
            const updatedItems = prevState.map((item) => {
              const matchingItem = resp.items.find(
                (respItem) => respItem.employee.id === item.employee.id
              );

              return matchingItem || item;
            });

            return updatedItems;
          });
        })
        .catch(onErrorSnackbar)
        .finally(() => {
          setIsLoading(false);
        });
    },
    [onErrorSnackbar]
  );

  const isEmpty = useMemo(
    () => !items.length && !isLoading,
    [items.length, isLoading]
  );

  return {
    items,
    isLoading,
    isFetching,
    isEmpty,
    hasMore,
    fetchData,
    fetchEmployeeData,
  };
}
